import type { SessionCommand } from '@fitting-room/contracts'
import { StepShell } from '../../components/RoomChrome'
import { uiStrings } from '../../lib/strings'
import { text } from '../../lib/text'
import type { StepProps } from '../types'

/**
 * Which rail she would like to see first.
 *
 * This is the last screen before the gowns, and it is the whole of what used
 * to be a band floating over the questionnaire. The band appeared wherever her
 * answers happened to trip the boutique's rule — which on a phone meant a
 * second catalogue arriving over a question about her waist, taking 25% of the
 * screen and pushing the question she was answering below halfway.
 *
 * Asked here it is one decision in the one place it applies. It writes nothing
 * but the line: there is no wrong answer to recover from, and the switcher on
 * the deck itself remains the way to change her mind.
 *
 * The recommendation, when the shop has one, is `view.lineOffer` — the same
 * rules, the same words, and the same silence about a reason the boutique
 * would rather not say out loud. Nothing here decides whether to print a
 * reason, because a reason it may not print never arrives.
 */
export function LineChoiceStep({ step, ctx }: StepProps<'line_choice'>) {
  const { view, locale } = ctx
  const t = uiStrings(locale)

  const lines = view.flow.lines
  const current = view.session.lineKey ?? lines[0]?.key
  const suggested = view.lineOffer?.lineKey

  /*
   * A flow with no catalogues at all has nothing to ask. It should not be
   * authored with this step; if it is, she gets the heading and a way onward
   * rather than a dead end, because the one thing this screen must never do is
   * strand her between the questionnaire and the gowns.
   */
  if (lines.length === 0) {
    return (
      <StepShell
        title={text(step.title, locale)}
        body={text(step.body, locale)}
        footer={
          <button type="button" className="fr-btn fr-btn-primary w-full" disabled={ctx.pending} onClick={() => ctx.advance()}>
            {text(step.ctaLabel, locale) || t.continue}
          </button>
        }
      >
        <></>
      </StepShell>
    )
  }

  const choose = (lineKey: string) => {
    // The default line is stored as "no line", so choosing it clears rather
    // than sets — otherwise going back and forth would leave the session
    // pinned to a key the flow may later rename.
    const value = lineKey === lines[0]?.key ? null : lineKey
    const command: SessionCommand = { type: 'set_line', lineKey: value }
    ctx.advance([command])
  }

  return (
    <StepShell title={text(step.title, locale)} body={text(step.body, locale)}>
      {/*
        The reason sits above the choices rather than on one of them: it is the
        shop explaining itself, not a label on a button.
      */}
      {view.lineOffer?.reason ? (
        <p className="mb-4 text-[0.9375rem] leading-relaxed" style={{ color: 'var(--fr-ink-dim)' }}>
          {text(view.lineOffer.reason, locale)}
        </p>
      ) : null}

      <ul className="flex flex-col gap-3">
        {lines.map((line) => {
          const isSuggested = line.key === suggested
          return (
            <li key={line.key}>
              <button
                type="button"
                className="fr-card-face w-full p-4 text-left"
                disabled={ctx.pending}
                onClick={() => choose(line.key)}
                aria-current={line.key === current ? 'true' : undefined}
                style={{
                  borderColor: isSuggested ? 'var(--fr-primary)' : 'var(--fr-rule)',
                  background: isSuggested
                    ? 'color-mix(in srgb, var(--fr-primary) 10%, var(--fr-surface))'
                    : 'var(--fr-surface)',
                }}
              >
                <span className="flex items-baseline justify-between gap-3">
                  <span className="fr-display text-[1.25rem]">{text(line.label, locale)}</span>
                  {isSuggested ? (
                    <span className="fr-eyebrow shrink-0" style={{ color: 'var(--fr-primary-deep)' }}>
                      {t.suggested}
                    </span>
                  ) : null}
                </span>
                {line.caption ? (
                  <span className="mt-1.5 block text-[0.8125rem] leading-snug" style={{ color: 'var(--fr-ink-dim)' }}>
                    {text(line.caption, locale)}
                  </span>
                ) : null}
              </button>
            </li>
          )
        })}
        {/*
          "Not sure yet" lands where choosing the first line would — the default
          catalogue — and says nothing more about her than that. Drawn as the
          way past rather than as a third catalogue, because it is not one.
        */}
        {step.undecided ? (
          <li>
            <button
              type="button"
              className="fr-card-face w-full p-4 text-left"
              disabled={ctx.pending}
              onClick={() => ctx.advance([{ type: 'set_line', lineKey: null }])}
              style={{ borderStyle: 'dashed', borderColor: 'var(--fr-rule)', background: 'transparent' }}
            >
              <span className="fr-display block text-[1.25rem]" style={{ color: 'var(--fr-ink-dim)' }}>
                {text(step.undecided.label, locale)}
              </span>
              {step.undecided.caption ? (
                <span className="mt-1.5 block text-[0.8125rem] leading-snug" style={{ color: 'var(--fr-ink-faint)' }}>
                  {text(step.undecided.caption, locale)}
                </span>
              ) : null}
            </button>
          </li>
        ) : null}
      </ul>
    </StepShell>
  )
}
